"use client";

import { Coins, TriangleAlert, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCredits } from "@/lib/utils/credit.utils";
import { getOperationLabel } from "@/lib/utils/credit-guard.utils";
import type { InsufficientCreditsContext } from "@/lib/types/credit-guard.types";

interface CreditGuardModalProps {
  open: boolean;
  onClose: () => void;
  context: InsufficientCreditsContext;
  /**
   * A team member who is not its admin cannot top up — their credits are
   * allocated from the team pool — so the modal explains that instead of
   * offering a purchase they can't make.
   */
  isTeamMember: boolean;
  onBuyCredits: () => void;
}

/**
 * Shown when an action costs more credits than the user has.
 *
 * Three states, because they are three different situations and the wording
 * that fits one is wrong for the others:
 *
 *   - EXHAUSTED (balance 0). Nothing works until they top up. The modal says
 *     that plainly and offers credits.
 *   - SHORT (some balance, not enough for this action). The interesting
 *     question is how far off they are, so the figures lead.
 *   - PARTIAL (enough to start, not enough to finish). Nothing is blocked —
 *     this one ASKS. The question it has to answer is "what happens to the
 *     rest?", so it leads with how many of them the balance covers and names
 *     the number in the button. Everything here is an estimate off the
 *     cheapest per-item price, and it says so rather than quoting a figure it
 *     can't stand behind.
 *
 * It never offers a plan upgrade. Running out of credits is not a plan
 * problem — a paid plan with an empty balance is in exactly this position —
 * and answering "you're out of credits" with "upgrade your plan" reads as a
 * sales prompt rather than the fix. The fix is credits, so that is the button.
 *
 * The three figures answer the three questions someone in this position asks,
 * in order: what have I got, what is the least I need to get going, and what
 * would the whole job cost. The last is an ESTIMATE and says so — a bulk
 * unlock charges per contact as it runs, so it is not a price quoted up front
 * (see CreditCheckResult.requiredCredits).
 */
export function CreditGuardModal({
  open,
  onClose,
  context,
  isTeamMember,
  onBuyCredits,
}: CreditGuardModalProps) {
  const {
    operation,
    requiredCredits,
    availableCredits,
    fullCost,
    count,
    customMessage,
    itemNoun = "contact",
    partialRun,
  } = context;
  const label = getOperationLabel(operation);
  const isExhausted = availableCredits <= 0;
  const shortBy = count - (partialRun?.affordableCount ?? count);
  const items = `${formatCredits(count)} ${itemNoun}${count === 1 ? "" : "s"}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Esc, the overlay and the close button all land here. onClose settles
        // a pending partial-run prompt as "no" — see CreditGuardProvider.
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isExhausted ? (
              <Coins className="h-5 w-5 text-amber-500" />
            ) : (
              <TriangleAlert className="h-5 w-5 text-amber-500" />
            )}
            {partialRun
              ? "Not enough credits to finish"
              : isExhausted
                ? "You're out of credits"
                : "Not enough credits"}
          </DialogTitle>
          <DialogDescription className="pt-1">
            {customMessage ??
              (partialRun ? (
                <>
                  Your balance covers about{" "}
                  <span className="font-medium text-foreground">
                    {formatCredits(partialRun.affordableCount)} of {formatCredits(count)}
                  </span>
                  . The run stops when the credits run out, and the remaining{" "}
                  {formatCredits(shortBy)} will be skipped.
                </>
              ) : isExhausted ? (
                <>
                  Your balance is empty, so{" "}
                  <span className="font-medium text-foreground">{label}</span>{" "}
                  can&apos;t run. Top up and pick up where you left off.
                </>
              ) : (
                <>
                  <span className="font-medium text-foreground">{label}</span> needs{" "}
                  {formatCredits(requiredCredits)} credit
                  {requiredCredits === 1 ? "" : "s"} to start, and you have{" "}
                  {formatCredits(availableCredits)}.
                </>
              ))}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-hidden rounded-lg border border-border">
          {/* Amber for a partial run, red for a blocked one: nothing has been
              refused here, so the red of "you can't do this" would overstate it. */}
          <div
            className={
              partialRun
                ? "flex items-center justify-between bg-amber-50 px-4 py-3 dark:bg-amber-500/10"
                : "flex items-center justify-between bg-red-50 px-4 py-3 dark:bg-red-500/10"
            }
          >
            <span className="text-sm font-medium text-foreground">Your balance</span>
            <span
              className={
                partialRun
                  ? "text-sm font-bold tabular-nums text-amber-600 dark:text-amber-400"
                  : "text-sm font-bold tabular-nums text-red-600 dark:text-red-400"
              }
            >
              {formatCredits(availableCredits)} credits
            </span>
          </div>

          {partialRun ? (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-sm text-muted-foreground">
                Est. cost for {items}
              </span>
              <span className="text-sm font-semibold tabular-nums text-foreground">
                {formatCredits(fullCost)} credits
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-sm text-muted-foreground">Minimum to start</span>
              <span className="text-sm font-semibold tabular-nums text-foreground">
                {formatCredits(requiredCredits)} credits
              </span>
            </div>
          )}

          {partialRun ? (
            <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-3">
              <span className="text-sm text-muted-foreground">
                Covered by your balance
              </span>
              <span className="text-sm font-medium tabular-nums text-muted-foreground">
                ~{formatCredits(partialRun.affordableCount)} of {formatCredits(count)}
              </span>
            </div>
          ) : /* Only worth showing when it says something the row above doesn't. */
          count > 1 && fullCost > requiredCredits ? (
            <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-3">
              <span className="text-sm text-muted-foreground">
                Est. cost for {items}
              </span>
              <span className="text-sm font-medium tabular-nums text-muted-foreground">
                {formatCredits(fullCost)} credits
              </span>
            </div>
          ) : null}
        </div>

        {isTeamMember ? (
          <div className="flex items-start gap-2 rounded-lg border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-muted-foreground">
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-brand/70" />
            <p>
              Your credits are allocated from your team&apos;s pool. Ask your team
              admin to allocate more
              {partialRun ? (
                <> to cover all {items}.</>
              ) : (
                <>, and {label.toLowerCase()} will work straight away.</>
              )}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {partialRun
              ? `Top up first to run all ${items} in one go.`
              : `Top up your balance to carry on using ${label.toLowerCase()}.`}
          </p>
        )}

        <DialogFooter className="gap-3 sm:gap-3">
          {/* The partial variant is a question, so its primary action is the
              answer the user came for. Cancelling is the dialog's own close
              button and Esc, both of which decline the run. */}
          {partialRun ? (
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={isTeamMember ? onClose : onBuyCredits}
              >
                {isTeamMember ? "Cancel" : "Buy more credits"}
              </Button>
              <Button
                className="flex-1 font-semibold"
                onClick={() => partialRun.onConfirm(true)}
              >
                Continue with ~{formatCredits(partialRun.affordableCount)}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="flex-1" onClick={onClose}>
                {isTeamMember ? "Got it" : "Cancel"}
              </Button>
              {isTeamMember ? null : (
                <Button className="flex-1 font-semibold" onClick={onBuyCredits}>
                  Buy more credits
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreditGuardModal;
